// src/admin/components/editor/FloatingMenuBar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  Bold, 
  Italic, 
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List, 
  ListOrdered,
  Quote,
  Link,
  Image as ImageIcon,
  X
} from 'lucide-react';

const FloatingMenuBar = ({ editor, isDark }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const menuRef = useRef(null);

  useEffect(() => {
    if (!editor) return;

    const updatePosition = () => {
      const { view, state } = editor;
      const { from, to } = state.selection;
      
      // Check if there's a selection
      if (from !== to) {
        const coords = view.coordsAtPos(from);
        const selectionRect = window.getSelection()?.getRangeAt(0)?.getBoundingClientRect();
        
        if (coords) {
          // Position the menu above the selection
          const top = coords.top - 60;
          const left = coords.left + (coords.right - coords.left) / 2;
          
          setPosition({
            top: Math.max(10, top), // Don't go above the viewport
            left: Math.max(10, left), // Don't go off screen left
          });
          setIsVisible(true);
        }
      } else {
        setIsVisible(false);
        setShowLinkInput(false);
      }
    };

    // Update position on selection change
    editor.on('selectionUpdate', updatePosition);
    editor.on('focus', updatePosition);
    
    // Also update on scroll
    const handleScroll = () => {
      if (isVisible) {
        updatePosition();
      }
    };
    window.addEventListener('scroll', handleScroll);

    // Click outside to close
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsVisible(false);
        setShowLinkInput(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      editor.off('selectionUpdate', updatePosition);
      editor.off('focus', updatePosition);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editor, isVisible]);

  // Handle link insertion
  const handleLinkInsert = () => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
      setLinkUrl('');
      setShowLinkInput(false);
    }
  };

  // Handle image upload
  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          editor.chain().focus().setImage({ src: e.target.result }).run();
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  if (!isVisible || !editor) return null;

  // Check if we can apply formatting
  const canBold = editor.can().toggleBold();
  const canItalic = editor.can().toggleItalic();
  const canStrike = editor.can().toggleStrike();

  const buttonClass = (isActive = false) => `
    p-1.5 rounded hover:bg-blue-500/10 transition-colors
    ${isActive ? 'text-blue-800 bg-blue-500/10' : ''}
    disabled:opacity-30 disabled:cursor-not-allowed
  `;

  return (
    <div
      ref={menuRef}
      className={`
        fixed z-50 p-1.5 rounded-lg shadow-xl
        ${isDark ? 'bg-gray-800 border border-white/10' : 'bg-white border border-gray-200'}
        flex items-center gap-0.5
        transition-opacity duration-200
        min-h-[40px]
      `}
      style={{
        top: position.top,
        left: position.left,
        transform: 'translateX(-50%)',
      }}
    >
      {showLinkInput ? (
        // Link Input Mode
        <div className="flex items-center gap-2 px-2">
          <input
            type="text"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="Enter URL..."
            className={`
              px-2 py-1 rounded text-sm outline-none
              ${isDark ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-900'}
              min-w-[200px]
            `}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleLinkInsert();
              if (e.key === 'Escape') {
                setShowLinkInput(false);
                setLinkUrl('');
              }
            }}
          />
          <button
            onClick={handleLinkInsert}
            className="p-1 rounded bg-blue-800 text-white hover:bg-blue-700 transition-colors"
            disabled={!linkUrl}
          >
            <Link size={14} />
          </button>
          <button
            onClick={() => {
              setShowLinkInput(false);
              setLinkUrl('');
            }}
            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        // Regular Toolbar
        <>
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={buttonClass(editor.isActive('bold'))}
            disabled={!canBold}
            title="Bold (Ctrl+B)"
          >
            <Bold size={16} />
          </button>
          
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={buttonClass(editor.isActive('italic'))}
            disabled={!canItalic}
            title="Italic (Ctrl+I)"
          >
            <Italic size={16} />
          </button>
          
          <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={buttonClass(editor.isActive('strike'))}
            disabled={!canStrike}
            title="Strikethrough"
          >
            <Strikethrough size={16} />
          </button>
          
          <div className="w-px h-6 bg-gray-300/30 mx-0.5" />
          
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={buttonClass(editor.isActive('heading', { level: 1 }))}
            title="Heading 1"
          >
            <Heading1 size={16} />
          </button>
          
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={buttonClass(editor.isActive('heading', { level: 2 }))}
            title="Heading 2"
          >
            <Heading2 size={16} />
          </button>
          
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={buttonClass(editor.isActive('heading', { level: 3 }))}
            title="Heading 3"
          >
            <Heading3 size={16} />
          </button>
          
          <div className="w-px h-6 bg-gray-300/30 mx-0.5" />
          
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={buttonClass(editor.isActive('bulletList'))}
            title="Bullet List"
          >
            <List size={16} />
          </button>
          
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={buttonClass(editor.isActive('orderedList'))}
            title="Numbered List"
          >
            <ListOrdered size={16} />
          </button>
          
          <button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={buttonClass(editor.isActive('blockquote'))}
            title="Blockquote"
          >
            <Quote size={16} />
          </button>
          
          <div className="w-px h-6 bg-gray-300/30 mx-0.5" />
          
          <button
            onClick={() => setShowLinkInput(true)}
            className={buttonClass(editor.isActive('link'))}
            title="Insert Link"
          >
            <Link size={16} />
          </button>
          
          <button
            onClick={handleImageUpload}
            className={buttonClass(false)}
            title="Insert Image"
          >
            <ImageIcon size={16} />
          </button>
        </>
      )}
    </div>
  );
};

export default FloatingMenuBar;